import React, { useMemo, useState } from 'react';
import { Plus, Save, Trash2 } from 'lucide-react';
import { MONTH_LABELS } from '../../../placeholders/periodSummaryUtils';
import { addBudgetLine, deleteBudgetLine, updateBudgetLine } from '../../../../services/budgetAPI';
import { buildFormStyles, money, Pill, TreasuryDrawer } from '../treasury/treasuryUi';
import { flattenBudgetAccounts } from './BudgetAccountsPanel';

const accountLeaf = (row) =>
  [
    row.detail_level_6,
    row.detail_level_5,
    row.detail_level_4,
    row.detail_level_3,
    row.detail_level_2,
    row.detail_level_1,
    row.subaccount,
    row.account,
    row.category,
  ]
    .map((value) => String(value || '').trim())
    .find(Boolean) || row.account_code || 'Cuenta';

const parseMonths = (values) =>
  Array.from({ length: 12 }).map((_, index) => Number(String(values?.[index] ?? '0').replace(',', '.')) || 0);

const BudgetLinesPanel = ({
  ui,
  isDarkMode = false,
  budget = null,
  chartAccounts = [],
  budgetAccounts = [],
  canEdit = false,
  onChanged,
}) => {
  const styles = buildFormStyles(ui);
  const [natureFilter, setNatureFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [drafts, setDrafts] = useState({});
  const [showAdd, setShowAdd] = useState(false);
  const [addAccountId, setAddAccountId] = useState('');
  const [addAnnual, setAddAnnual] = useState('');
  const [addNature, setAddNature] = useState('gasto');
  const [busyId, setBusyId] = useState('');
  const [error, setError] = useState('');

  const usedAccountIds = useMemo(
    () => new Set((budget?.lines || []).filter((line) => line.id).map((line) => Number(line.chart_account_id))),
    [budget]
  );
  const usedBudgetAccountIds = useMemo(
    () =>
      new Set(
        (budget?.lines || [])
          .filter((line) => line.id && line.budget_account_id)
          .map((line) => Number(line.budget_account_id))
      ),
    [budget]
  );

  const allBudgetAccounts = useMemo(() => flattenBudgetAccounts(budgetAccounts), [budgetAccounts]);
  const useBudgetTree = allBudgetAccounts.length > 0;
  const budgetAccountOptions = useMemo(
    () => allBudgetAccounts.filter((row) => !usedBudgetAccountIds.has(Number(row.id))),
    [allBudgetAccounts, usedBudgetAccountIds]
  );

  const accountOptions = useMemo(
    () =>
      (chartAccounts || [])
        .filter((row) => !usedAccountIds.has(Number(row.id)))
        .map((row) => ({
          id: row.id,
          label: `${row.account_code} · ${accountLeaf(row)}`,
        }))
        .sort((a, b) => a.label.localeCompare(b.label, 'es')),
    [chartAccounts, usedAccountIds]
  );

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (budget?.lines || []).filter((line) => {
      if (natureFilter !== 'all' && line.line_nature !== natureFilter) return false;
      if (!q) return true;
      return `${line.account_code} ${line.account_label}`.toLowerCase().includes(q);
    });
  }, [budget, natureFilter, search]);

  const monthsOf = (line) => drafts[line.id]?.months || line.months || Array(12).fill(0);

  const setMonth = (line, index, value) => {
    if (!line.id) return;
    setDrafts((prev) => {
      const current = prev[line.id]?.months || [...(line.months || Array(12).fill(0))];
      const next = [...current];
      next[index] = value;
      return { ...prev, [line.id]: { months: next } };
    });
  };

  const isDirty = (line) => {
    const draft = drafts[line.id];
    if (!draft) return false;
    return parseMonths(draft.months).some((value, index) => value !== Number(line.months?.[index] || 0));
  };

  const handleSave = async (line) => {
    if (!line.id) return;
    setBusyId(line.id);
    setError('');
    try {
      const next = await updateBudgetLine(budget.id, line.id, { months: parseMonths(monthsOf(line)) });
      setDrafts((prev) => {
        const copy = { ...prev };
        delete copy[line.id];
        return copy;
      });
      onChanged?.(next);
    } catch (err) {
      setError(err.message || 'No se pudo guardar la línea.');
    } finally {
      setBusyId('');
    }
  };

  const handleDistribute = (line) => {
    const total = Number(window.prompt('Monto anual a distribuir en 12 meses', String(line.budget_total || '')) || '');
    if (!Number.isFinite(total) || total === 0) return;
    const monthly = Math.round((total / 12) * 100) / 100;
    const months = Array(12).fill(monthly);
    months[11] = Math.round((total - monthly * 11) * 100) / 100;
    setDrafts((prev) => ({ ...prev, [line.id]: { months } }));
  };

  const handleDelete = async (line) => {
    if (!line.id || !window.confirm(`¿Quitar ${line.account_code} del presupuesto?`)) return;
    setBusyId(line.id);
    setError('');
    try {
      const next = await deleteBudgetLine(budget.id, line.id);
      onChanged?.(next);
    } catch (err) {
      setError(err.message || 'No se pudo eliminar la línea.');
    } finally {
      setBusyId('');
    }
  };

  const handleAdd = async () => {
    const accountId = Number(addAccountId);
    if (!accountId) return;
    const annual = Number(String(addAnnual).replace(',', '.')) || 0;
    const monthly = annual ? Math.round((annual / 12) * 100) / 100 : 0;
    const months = annual ? Array(12).fill(monthly) : Array(12).fill(0);
    if (annual) months[11] = Math.round((annual - monthly * 11) * 100) / 100;
    setBusyId('add');
    setError('');
    try {
      const next = await addBudgetLine(budget.id, {
        ...(useBudgetTree ? { budget_account_id: accountId } : { chart_account_id: accountId }),
        months,
        line_nature: addNature,
      });
      setShowAdd(false);
      setAddAccountId('');
      setAddAnnual('');
      onChanged?.(next);
    } catch (err) {
      setError(err.message || 'No se pudo agregar la cuenta.');
    } finally {
      setBusyId('');
    }
  };

  const handlePromote = async (line) => {
    setBusyId(`promo-${line.chart_account_id}`);
    setError('');
    try {
      const next = await addBudgetLine(budget.id, {
        chart_account_id: line.chart_account_id,
        months: Array(12).fill(0),
        line_nature: line.line_nature,
      });
      onChanged?.(next);
    } catch (err) {
      setError(err.message || 'No se pudo agregar la cuenta.');
    } finally {
      setBusyId('');
    }
  };

  const th = {
    textAlign: 'right',
    padding: '8px 6px',
    borderBottom: `1px solid ${ui.border}`,
    fontSize: 11,
    color: ui.muted,
    whiteSpace: 'nowrap',
    background: ui.header,
  };

  return (
    <div style={{ background: ui.panel, border: `1px solid ${ui.border}`, borderRadius: 10, padding: 12 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap', marginBottom: 10 }}>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Buscar cuenta…"
            style={{ ...styles.input, width: 240 }}
          />
          <select value={natureFilter} onChange={(event) => setNatureFilter(event.target.value)} style={{ ...styles.input, width: 160 }}>
            <option value="all">Ingresos y gastos</option>
            <option value="ingreso">Ingresos</option>
            <option value="gasto">Gastos</option>
          </select>
        </div>
        {canEdit ? (
          <button type="button" style={styles.primaryBtn} onClick={() => setShowAdd(true)}>
            <Plus size={14} /> Agregar cuenta
          </button>
        ) : null}
      </div>
      {error ? <p style={{ margin: '0 0 10px', color: ui.danger, fontSize: 12 }}>{error}</p> : null}
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 1100 }}>
          <thead>
            <tr>
              <th style={{ ...th, textAlign: 'left', position: 'sticky', left: 0, zIndex: 1 }}>Cuenta</th>
              {MONTH_LABELS.map((label) => (
                <th key={label} style={th}>
                  {label}
                </th>
              ))}
              <th style={th}>Presupuesto</th>
              <th style={th}>Real</th>
              <th style={th}>Variación</th>
              <th style={th} />
            </tr>
          </thead>
          <tbody>
            {rows.map((line) => {
              const months = monthsOf(line);
              const budgetTotal = months.reduce((sum, value) => sum + Number(value || 0), 0);
              const variance = Number(line.actual_total || 0) - budgetTotal;
              return (
                <tr key={line.id || `actual-${line.chart_account_id}`}>
                  <td
                    style={{
                      padding: '8px 6px',
                      position: 'sticky',
                      left: 0,
                      background: ui.panel,
                      minWidth: 220,
                    }}
                  >
                    <div style={{ fontWeight: 700, fontSize: 12, color: ui.text }}>{line.account_code}</div>
                    <div style={{ fontSize: 11, color: ui.muted }}>{line.account_label}</div>
                    <Pill
                      label={line.line_nature === 'ingreso' ? 'Ingreso' : 'Gasto'}
                      tone={line.line_nature === 'ingreso' ? 'success' : 'warning'}
                      isDarkMode={isDarkMode}
                    />
                  </td>
                  {months.map((value, index) => (
                    <td key={`${line.chart_account_id}-${index}`} style={{ padding: '4px 4px', textAlign: 'right' }}>
                      {canEdit && line.id ? (
                        <input
                          value={value}
                          onChange={(event) => setMonth(line, index, event.target.value)}
                          style={{ ...styles.input, height: 28, width: 88, textAlign: 'right', padding: '0 6px' }}
                        />
                      ) : (
                        <span style={{ fontSize: 12 }}>{money(value)}</span>
                      )}
                    </td>
                  ))}
                  <td style={{ padding: '8px 6px', textAlign: 'right', fontWeight: 700, fontSize: 12 }}>{money(budgetTotal)}</td>
                  <td style={{ padding: '8px 6px', textAlign: 'right', fontSize: 12 }}>{money(line.actual_total)}</td>
                  <td
                    style={{
                      padding: '8px 6px',
                      textAlign: 'right',
                      fontSize: 12,
                      fontWeight: 700,
                      color: variance >= 0 ? '#047857' : '#be185d',
                    }}
                  >
                    {money(variance)}
                  </td>
                  <td style={{ padding: '8px 6px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                    {canEdit && line.id ? (
                      <>
                        <button type="button" style={styles.ghostBtn} onClick={() => handleDistribute(line)}>
                          12 meses
                        </button>
                        <button
                          type="button"
                          style={styles.primaryBtn}
                          onClick={() => handleSave(line)}
                          disabled={!isDirty(line) || busyId === line.id}
                        >
                          <Save size={13} /> {busyId === line.id ? '…' : 'Guardar'}
                        </button>
                        <button type="button" style={styles.ghostBtn} onClick={() => handleDelete(line)}>
                          <Trash2 size={13} />
                        </button>
                      </>
                    ) : canEdit && !line.id ? (
                      <button
                        type="button"
                        style={styles.ghostBtn}
                        onClick={() => handlePromote(line)}
                        disabled={busyId === `promo-${line.chart_account_id}`}
                      >
                        Agregar
                      </button>
                    ) : null}
                  </td>
                </tr>
              );
            })}
            {!rows.length ? (
              <tr>
                <td colSpan={17} style={{ padding: 20, textAlign: 'center', color: ui.muted, fontSize: 13 }}>
                  No hay líneas en este presupuesto.
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>

      {showAdd ? (
        <TreasuryDrawer
          ui={ui}
          title="Agregar cuenta"
          subtitle="La cuenta entra al presupuesto del año seleccionado."
          error={error}
          saving={busyId === 'add'}
          onClose={() => setShowAdd(false)}
          onSave={handleAdd}
          saveLabel="Agregar"
        >
          <label style={styles.label}>
            {useBudgetTree ? 'Cuenta de presupuesto' : 'Cuenta contable'}
            <select value={addAccountId} onChange={(event) => setAddAccountId(event.target.value)} style={styles.input}>
              <option value="">Selecciona una cuenta</option>
              {(useBudgetTree ? budgetAccountOptions : accountOptions).map((option) => (
                <option key={option.id} value={option.id}>
                  {option.optionLabel || option.label}
                </option>
              ))}
            </select>
          </label>
          <label style={styles.label}>
            Naturaleza
            <select value={addNature} onChange={(event) => setAddNature(event.target.value)} style={styles.input}>
              <option value="ingreso">Ingreso</option>
              <option value="gasto">Gasto</option>
            </select>
          </label>
          <label style={styles.label}>
            Monto anual (opcional, se reparte en 12 meses)
            <input value={addAnnual} onChange={(event) => setAddAnnual(event.target.value)} style={styles.input} />
          </label>
        </TreasuryDrawer>
      ) : null}
    </div>
  );
};

export default BudgetLinesPanel;
